import type { PlanningSection } from '~/types/overview';
import type { OutlineActionEvent } from '~/utils/sse-parser';
import { desiredAgentDocs } from '~/design-system/components/DeckGen/birtvy/birtvyAgentBridge';

/** Decode storage envelopes, not arbitrary JSON-looking prose. Never strip meaningful backslashes. */
export function readableContent(value: string | null | undefined): string {
  let text = value ?? '';
  for (let i = 0; i < 3 && text.trim().startsWith('"'); i++) {
    try { const parsed: unknown = JSON.parse(text); if (typeof parsed !== 'string') break; text = parsed; }
    catch { break; }
  }
  return text;
}

export function sectionText(section: Pick<PlanningSection, 'content' | 'pendingContent'> | undefined): string {
  return readableContent(section?.pendingContent?.trim() ? section.pendingContent : section?.content);
}

export function planningDocs(sections: PlanningSection[]) {
  // Historical model-written planning sections sometimes escaped Markdown twice. Repair only
  // that known prose format, never arbitrary Library documents, code, or Windows paths.
  return desiredAgentDocs(sections).map(doc => ({ ...doc, content: readablePlanningContent(doc.content) }));
}

export function readablePlanningContent(value: string | null | undefined) {
  return readableContent(value).replace(/\\n(?=\\n|#{1,6} |[-*] |[A-Z])/g, '\n').replace(/\\n(?=\n)/g, '\n');
}

/** This is a saved outline snapshot, not an artifact-generation progress report. */
export function outlineSnapshot(content: string): OutlineActionEvent {
  try {
    const data = JSON.parse(content);
    return { status: 'complete', chapters: [
      ...(data.cover?.title ? [{ name: 'Cover', canvases: [{ name: data.cover.title, status: 'complete' }] }] : []),
      ...(data.chapters ?? []).map((chapter: { title?: string; canvases?: { title?: string }[] }) => ({
        name: chapter.title, canvases: (chapter.canvases ?? []).map(canvas => ({ name: canvas.title, status: 'complete' })),
      })),
    ] };
  } catch { return { status: 'complete', chapters: [] }; }
}

/** Stable view-only IDs cannot collide with the blob's positive, persisted IDs. */
export function resourceDocId(key: string): number {
  let hash = 2166136261;
  for (const ch of key) hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619);
  return -(hash >>> 0) - 1;
}
